/**
 * Two-column sortable card grid, with the original's "not in app grid" divider
 * dropped in before the first card that falls outside the top four active rows.
 */

import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import StyleCard from './StyleCard';
import { MAX_ACTIVE_PER_CATALOGUE } from './constants';
import { OrderItem, StyleFamily, StyleRow } from './types';

export interface StyleCardGridProps {
  orderedItems: OrderItem[];
  rowsByKey: Map<string, StyleRow>;
  family: StyleFamily;
  pillLabel: string;
  isMostPopularCatalogue: boolean;
  loading: boolean;
  expandedKeys: Set<string>;
  busyKey: string | null;
  onToggle: (row: StyleRow, nextActive: boolean) => void;
  onEdit: (row: StyleRow) => void;
  onDelete: (row: StyleRow) => void;
  onExpandToggle: (key: string) => void;
  onMove: (key: string, direction: -1 | 1) => void;
  onCopy: (text: string, label: string) => void;
}

const StyleCardGrid: React.FC<StyleCardGridProps> = ({
  orderedItems,
  rowsByKey,
  family,
  pillLabel,
  isMostPopularCatalogue,
  loading,
  expandedKeys,
  busyKey,
  onToggle,
  onEdit,
  onDelete,
  onExpandToggle,
  onMove,
  onCopy,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', p: 2 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: '10px' }} />
        ))}
      </Box>
    );
  }

  if (orderedItems.length === 0) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
          No styles in this catalogue yet.
        </Typography>
      </Box>
    );
  }

  const activeOrdered = orderedItems.filter((i) => i.isActive);
  const inGridKeys = new Set(activeOrdered.slice(0, MAX_ACTIVE_PER_CATALOGUE).map((i) => i.key));

  return (
    <Box sx={{ p: 2, overflowY: 'auto', minHeight: 0 }}>
      <SortableContext items={orderedItems.map((i) => i.key)} strategy={rectSortingStrategy}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: '8px' }}>
          {orderedItems.map((orderItem, idx) => {
            const row = rowsByKey.get(orderItem.key);
            if (!row) return null;

            const activePosition = activeOrdered.findIndex((i) => i.key === orderItem.key);
            const inGrid = inGridKeys.has(orderItem.key);
            const isMostPopular = activePosition === 0 && isMostPopularCatalogue;

            const prevItem = idx > 0 ? orderedItems[idx - 1] : null;
            const prevInGrid = prevItem ? inGridKeys.has(prevItem.key) : true;
            const showDivider = !inGrid && prevInGrid && idx > 0;

            return (
              <React.Fragment key={orderItem.key}>
                {showDivider && (
                  <Box
                    sx={{
                      gridColumn: '1 / -1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      px: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, height: 1, bgcolor: '#d1d5db' }} />
                    <Typography
                      sx={{
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: '0.68rem',
                        color: '#94a3b8',
                        whiteSpace: 'nowrap',
                        fontWeight: 500,
                      }}
                    >
                      Not in app grid — drag card up or into a grid slot
                    </Typography>
                    <Box sx={{ flex: 1, height: 1, bgcolor: '#d1d5db' }} />
                  </Box>
                )}
                <StyleCard
                  row={row}
                  rowKeyValue={orderItem.key}
                  position={idx + 1}
                  inGrid={inGrid}
                  isMostPopular={isMostPopular}
                  isExpanded={expandedKeys.has(orderItem.key)}
                  family={family}
                  pillLabel={pillLabel}
                  busy={busyKey === orderItem.key}
                  canMoveUp={idx > 0}
                  canMoveDown={idx < orderedItems.length - 1}
                  onToggle={onToggle}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onExpandToggle={onExpandToggle}
                  onMove={onMove}
                  onCopy={onCopy}
                />
              </React.Fragment>
            );
          })}
        </Box>
      </SortableContext>
    </Box>
  );
};

export default StyleCardGrid;
